import React from 'react';
import {
    IColumn,
    Fabric,
    DetailsListLayoutMode,
    SelectionMode,
    Check,
    ShimmeredDetailsList,
    DetailsRow,
    IDetailsRowProps,
    mergeStyleSets,
    Label,
    Link,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
} from '@fluentui/react';
import { DefaultButton, IButton } from '@fluentui/react/lib/Button';
import { RouteComponentProps } from 'react-router-dom';
import { msalAuth } from '../../shared/auth/MsalAuthProvider';
import { IUser } from '../../shared/models/User.model';
import { MyAccessRequestCollection } from '../../shared/models/MyAccessRequest.model';
import { UIConstants, isMercuryTenant, isMSSalesTenant, MS_PER_DAY, EXPIRY_WARNING_COLOR, EXPIRY_WARNING_DAYS } from '../../shared/models/Constants';
import { RoleEligibilityEntry } from './MyAccessRequests.types';
import AccessRequestDetails from './AccessRequestDetails'
import ITenant from '../../shared/models/Tenant.model';
import { Utility } from '../../shared/models/Helper';
import AccessAPI from '../../store/AccessAPI';
import { MyAccessRequestResponse } from '../../shared/models/UserAccess.model';
import ConfigurationAPI from '../../shared/api/Configuration.api';
import { AllowedAccount } from '../../shared/models/Role.model';
import { AllowedAccountByTenant } from "../../shared/models/AllowedAccountTypes";
import { executeCancelSelected, executeEditSelected, executeRemoveSelected, executeRenewSelected } from './MyAccessRequests.actions';
import { BatchExecutionResult, LifecycleGridItem, RowExecutionState, RowExecutionUpdater } from './MyAccessRequests.types';
import { buildLifecycleItems } from './MyAccessRequests.lifecycleItems';
import { buildMercuryCurrentAccess, buildNonMercuryApprovedAccesses, buildNonMercuryEligibilityMap } from './MyAccessRequests.currentAccess';
import { getExecutionStateColor, getExecutionStateLabel } from './MyAccessRequests.rowExecution';
import MyAccessRequestsSelectionBar from './MyAccessRequests.SelectionBar';
import ViewPermissions from '../../shared/components/ViewPermissions';
const classNames = mergeStyleSets({
    compactCard: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    expandedCard: {
        padding: '0px 24px'
    },
    item: {
        selectors: {
            '&:hover': {
                textDecoration: 'underline',
                cursor: 'pointer'
            }
        }
    },
    statusAttestationExpired: {
        color: '#A4262C',
        fontWeight: 600,
        selectors: {
            '&:hover': {
                color: '#A4262C',
                textDecoration: 'underline'
            },
            '&:focus': {
                color: '#A4262C',
                outline: '2px solid #0078d4',
                outlineOffset: '1px'
            }
        }
    },
    srOnly: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0
    },
    gridCheckFix: {
        selectors: {
            // Increase specificity to override Fluent UI default check alignment
            '& .ms-Check': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            },
            '& .ms-Check-check': {
                top: 0,
                marginTop: 0,
                position: 'relative',
            },
        },
    },
});

const disabledLinkStyle = (base?: React.CSSProperties): React.CSSProperties => ({
    ...base,
    color: '#a19f9d',
    cursor: 'default',
    pointerEvents: 'none',
});

export interface MyAccessList {
    key: number;
    userName: string;
    role: string;
    requestedOn: string;
    status: string;
    remarks: string;
}

export interface MyAccessProps {
    applicationData: any;
    getAppData: any;
}

export interface MyAccessState {
    items: MyAccessList[];
    selectedPageIndex: number;
    userDetails: IUser;
    showModal: boolean;
    requestId: string;
    requestedBy: string;
    data: MyAccessRequestCollection;
    AccessData: MyAccessRequestResponse;
    allowedAccountTypes: AllowedAccount[];
    tenantName: string;
    searchValue: string;
    showRemoveConfirmation: boolean;
    showCancelConfirmation: boolean;
    actionInProgress: boolean;
    roleToRemove: LifecycleGridItem | null;
    requestIdToCancel: string | null;
    message: string;
    messageType: MessageBarType;
    showMessage: boolean;
    currentAccessMap: { [roleId: string]: string };
    approvedAccesses: Array<{ roleDefinitionId?: string; endDate?: string; allowRemove?: boolean }>;
    roleEligibilityMap: { [roleId: string]: RoleEligibilityEntry };
    hasRolePermissions: boolean;
    showPermissionsModal: boolean;
    clickedBusinessGrp: string | null;
    clickedRoleName: string | null;
    clickedRoleId: string | null;
    accessExpiryReminderWindowDays: number;
    sortColumnKey: string;
    isSortDescending: boolean;
    selectedItems: LifecycleGridItem[];
    rowExecutionStates: { [requestId: string]: RowExecutionState };
    batchInProgress: boolean;
}
type MyAccessRequestProps = MyAccessProps & MyAccessState
    & RouteComponentProps<{}>;


export default class MyAccessRequests extends React.Component<MyAccessRequestProps, MyAccessState>  {
    private _allItems: MyAccessList[] = [];
    private tenantData: ITenant = null;
    private accessHistoryPageRef = React.createRef<HTMLDivElement>();
    private _isMounted = false;
    private _focusTimer: ReturnType<typeof setTimeout> | null = null;
    private _feedbackTimer: ReturnType<typeof setTimeout> | null = null;

    private safeSetState = (state: Partial<MyAccessState>): void => {
        if (this._isMounted) {
            this.setState(state as Pick<MyAccessState, keyof MyAccessState>);
        }
    };

    public async componentDidMount() {
        this._isMounted = true;
        let userAccount = msalAuth.getAccount();
        let user: IUser = { PrincipalId: userAccount.accountIdentifier, Alias: userAccount.userName, Name: userAccount.name };
        this.safeSetState({ userDetails: user });
        let TenantData = Utility.GetTenantData();
        let tenantName = (this.props.match.params as any).tenantName;
        this.safeSetState({ tenantName: tenantName })
        tenantName = tenantName.toUpperCase();
        this.tenantData = TenantData.filter(x => x.TenantName.toUpperCase() === tenantName)[0];
        this.safeSetState({ hasRolePermissions: this.tenantData?.HasRolePermissions || false });
        ConfigurationAPI.GetAccessExpiryReminderWindowDays().then((response) => {
            if (!this._isMounted) {
                return;
            }

            if (response !== null && response !== undefined) {
                this.safeSetState({ accessExpiryReminderWindowDays: response });
            }
        });
        this.ensureDataFetched();
        this.setFocusOnPageHeader();
    }

    public componentWillUnmount(): void {
        this._isMounted = false;
        if (this._focusTimer) {
            clearTimeout(this._focusTimer);
            this._focusTimer = null;
        }
        if (this._feedbackTimer) {
            clearTimeout(this._feedbackTimer);
            this._feedbackTimer = null;
        }
    }
    private ensureDataFetched() {
        let tenantId = this.tenantData?.TenantId;
        if (tenantId) {
            if (this.tenantData?.isExternalAADTenant) {
              ConfigurationAPI.getConfigurationByKey(
                UIConstants.Configuration.AllowedAccountTypes
              ).then((response: AllowedAccountByTenant[]) => {
                if (!this._isMounted) {
                        return;
                }
                if (response != null && response.length > 0) {
                  let allowedTypes = response.filter(
                    (x) =>
                      x.tenantName?.toLowerCase() ==
                      this.tenantData?.TenantName.toLowerCase()
                  );
                    this.safeSetState({
                    allowedAccountTypes: allowedTypes[0]?.allowedDomains?.map(
                      (domain) => {
                        return {
                          AllowedType: domain.allowedType,
                          DomainSuffix: domain.domainSuffix,
                          IsDynamic: domain.isDynamic,
                        } as AllowedAccount;
                      }
                    ),
                  });
                }
              });
            }
            AccessAPI.getMyAccessRequest(tenantId).then((res) => {
                            this.safeSetState({ AccessData: res });
            });
            
            AccessAPI.getMyAccess(tenantId).then((res) => {
                const approved = res?.Data?.approvedAccess || [];

                if (isMercuryTenant(this.tenantData?.TenantName)) {
                    const { currentAccessMap, approvedAccesses, eligibilityMap } = buildMercuryCurrentAccess(approved);
                    this.safeSetState({ currentAccessMap, approvedAccesses, roleEligibilityMap: eligibilityMap });
                } else {
                    const { currentAccessMap, approvedAccesses } = buildNonMercuryApprovedAccesses(approved);
                    this.safeSetState({ currentAccessMap, approvedAccesses });

                    AccessAPI.requestRoleByTenant(tenantId).then((rolesRes) => {
                        if (!this._isMounted) return;
                        const roleData: any[] = rolesRes?.Data || [];
                        const eligibilityMap = buildNonMercuryEligibilityMap(approved, roleData, this.tenantData.isExternalAADTenant);
                        this.safeSetState({ roleEligibilityMap: eligibilityMap });
                    });
                }
            });
        } else {
            // Error
        }
    }
    private confirmMsgYesBtnRef = React.createRef<IButton>();

    public state: MyAccessState = {
        userDetails: null,
        items: this._allItems,
        selectedPageIndex: 0,
        showModal: false,
        requestId: '',
        data: null,
        AccessData: { Data: [], IsDataLoaded: false, IsSuccess: true },
        requestedBy: '',
        allowedAccountTypes: [],
        tenantName: '',
        showRemoveConfirmation: false,
        showCancelConfirmation: false,
        actionInProgress: false,
        roleToRemove: null,
        requestIdToCancel: null,
        message: '',
        messageType: MessageBarType.info,
        showMessage: false,
        currentAccessMap: {},
        approvedAccesses: [],
        roleEligibilityMap: {},
        hasRolePermissions: false,
        showPermissionsModal: false,
        clickedBusinessGrp: null,
        clickedRoleName: null,
        clickedRoleId: null,
        accessExpiryReminderWindowDays: 21,
        searchValue: '', // Added for search bar
        sortColumnKey: 'requestDate',
        isSortDescending: false,
        selectedItems: [],
        rowExecutionStates: {},
        batchInProgress: false,
    }

    public render(): JSX.Element {
        const { AccessData, showModal, searchValue, showRemoveConfirmation, showCancelConfirmation,
            actionInProgress, roleToRemove, requestIdToCancel, showMessage, message, messageType,
            showPermissionsModal, clickedBusinessGrp, clickedRoleName, clickedRoleId,
            selectedItems, batchInProgress } = this.state;
        const gridItems = this.getItems();
        const selectedSet = new Set(selectedItems.map(s => s.requestId));

        return (<React.Fragment>
            <div id={'div-msg-area'} style={{ height: 50 }}></div>
            <div aria-label="My Request History Page Opened" style={{ outline: 'none' }} tabIndex={-1} ref={this.accessHistoryPageRef} id={'div-myaccess'}>
                <Fabric style={{
                    maxHeight: '80vh',
                    overflow: 'auto',
                    display: 'block',
                    margin: '0 auto',
                }} >
                    <Label style={{ paddingLeft: 10, fontSize: 20 }} ><h1 className={"h1"} style={{ paddingLeft: 0, fontSize: 20, margin: 0 }}>{UIConstants.PageTitle.MyRequestHistory}</h1></Label>
                    {/* Search Bar */}
                    <div style={{ padding: '10px 0 10px 10px' }}>
                        <input
                            type="text"
                            placeholder="Search by Request ID"
                            value={searchValue}
                            onChange={e => this.setState({ searchValue: e.target.value })}
                            style={{ width: 250, padding: '6px', fontSize: '16px' }}
                            aria-label="Search by Request ID"
                        />
                    </div>
                    {showMessage && (
                        <MessageBar messageBarType={messageType} isMultiline={false}
                            onDismiss={() => this.setState({ showMessage: false })} dismissButtonAriaLabel="Close">
                            {message}
                        </MessageBar>
                    )}
                    {showRemoveConfirmation && this._renderActionConfirmation(
                        UIConstants.Messages.RemoveRoleConfirmation.replace('#RoleName#', roleToRemove?.role),
                        () => this._executeRemove(roleToRemove)
                    )}
                    {showCancelConfirmation && this._renderActionConfirmation(
                        UIConstants.Messages.RemoveRequestConfirmation.replace('#Request#', requestIdToCancel),
                        () => this._executeCancel(requestIdToCancel)
                    )}
                    {actionInProgress && <Spinner size={SpinnerSize.small} label="Processing..." labelPosition="right" style={{ padding: '4px 0' }} />}
                    <div className={classNames.gridCheckFix}>
                    <ShimmeredDetailsList
                        setKey="items"
                        columns={this.getColumnDefintion(gridItems, selectedSet)}
                        items={gridItems}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        getKey={(item) => item?.requestId}
                        enableShimmer={!AccessData?.IsDataLoaded}
                        ariaLabelForShimmer="Content is being fetched"
                        ariaLabelForGrid="My Request History details"
                        listProps={{ renderedWindowsAhead: 0, renderedWindowsBehind: 0 }}
                        onColumnHeaderClick={this._onColumnClick}
                        detailsListStyles={{ root: { overflow: 'visible' } }}
                        onRenderRow={(props: IDetailsRowProps) => {
                            const isSelected = selectedSet.has((props.item as LifecycleGridItem)?.requestId);
                            return (
                                <div style={isSelected ? { background: '#f0f6ff' } : undefined}>
                                    <DetailsRow {...props} />
                                </div>
                            );
                        }}
                    />
                    </div>
                    {(showModal) && <AccessRequestDetails data={this.state.data} requestId={this.state.requestId} requestedBy={this.state.requestedBy} isAdminScreen={false} {...this.props} onDismiss={() => { this.setState({ showModal: false }) }} />}
                    {showPermissionsModal && (
                        <ViewPermissions
                            businessGroupName={clickedBusinessGrp || ''}
                            roleName={clickedRoleName || ''}
                            roleId={clickedRoleId || ''}
                            tenantId={this.tenantData?.TenantId}
                            {...this.props}
                            onDismiss={() => { this.setState({ showPermissionsModal: false }); }}
                        />
                    )}
                </Fabric>
            </div>
            <MyAccessRequestsSelectionBar
                selectedItems={selectedItems}
                batchInProgress={batchInProgress}
                onCancelSelected={this._executeBatchCancel}
                onRemoveSelected={this._executeBatchRemove}
                onClearSelection={this._clearSelection}
            />
        </React.Fragment>
        );
    }

    private setFocusOnPageHeader() {
        this._focusTimer = setTimeout(() => {
            this.accessHistoryPageRef.current?.focus();
        }, 2000)
    }

    private _renderActionConfirmation(message: string, onConfirm: () => void): JSX.Element {
        setTimeout(() => { this.confirmMsgYesBtnRef.current?.focus(); }, 200);
        return (
            <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}
                actions={
                    <div>
                        <DefaultButton style={{ height: 25 }} componentRef={this.confirmMsgYesBtnRef}
                            onClick={onConfirm}>Yes</DefaultButton>
                        <DefaultButton style={{ height: 25 }} onClick={this._dismissAction}>Cancel</DefaultButton>
                    </div>
                }>
                <div role="alert">{message}</div>
            </MessageBar>
        );
    }

    private _renderEndDateColumn(item: LifecycleGridItem): JSX.Element {
        if (!item.endDate || item.endDate === 'Not Applicable') {
            return <span>Not Applicable</span>;
        }

        const endDateTs = new Date(item.originalEndDate ?? item.endDate).getTime();
        const diffInDays = Math.floor((endDateTs - Date.now()) / MS_PER_DAY);
        const isExpiringSoon = !isNaN(diffInDays) && diffInDays >= 0 && diffInDays <= EXPIRY_WARNING_DAYS;
        return <span style={isExpiringSoon ? { color: EXPIRY_WARNING_COLOR } : undefined}>{item.endDate}</span>;
    }

    private _renderRequestedForColumn(item: LifecycleGridItem): JSX.Element {
        if (item.requestedFor != null) {
            return <div>{item.requestedFor}</div>;
        }

        if (item.role?.includes('Bulk Request')) {
            return <div>On Behalf (Bulk Request)</div>;
        }

        return <div>{item.requestedBy || ''}</div>;
    }

    private _renderActionColumn(item: LifecycleGridItem): JSX.Element {
        const { selectedItems, batchInProgress, rowExecutionStates } = this.state;

        // Per-row execution feedback (after or during batch)
        const executionState = rowExecutionStates[item.requestId];
        if (executionState) {
            return (
                <span style={{ color: getExecutionStateColor(executionState.status), fontSize: 12 }}>
                    {getExecutionStateLabel(executionState)}
                </span>
            );
        }

        // Spinner for item currently being processed in batch
        if (batchInProgress && selectedItems.some(s => s.requestId === item.requestId)) {
            return <Spinner size={SpinnerSize.xSmall} />;
        }

        // Greyed-out when selection mode is active — row actions are disabled
        const hasSelection = selectedItems.length > 0;

        return (
            <div>
                {item.canEdit && (
                    <Link role="button" aria-label={`Edit access ${item.role}`}
                        onClick={hasSelection ? undefined : (e) => { e.stopPropagation(); this._clearSelection(); this._executeEdit(item); }}
                        style={hasSelection ? disabledLinkStyle({ marginRight: 8 }) : { marginRight: 8 }}>
                        Edit
                    </Link>
                )}
                {item.canRenew && (
                    <Link role="button" aria-label={`Renew access ${item.role}`}
                        onClick={hasSelection ? undefined : (e) => { e.stopPropagation(); this._clearSelection(); this._executeRenew(item); }}
                        style={hasSelection ? disabledLinkStyle({ marginRight: 8 }) : { marginRight: 8 }}>
                        Renew
                    </Link>
                )}
                {item.canCancel && (
                    <Link role="button" aria-label={`Cancel request ${item.requestId}`}
                        onClick={hasSelection ? undefined : (e) => { e.stopPropagation(); this._onCancelBtnClick(item); }}
                        style={hasSelection ? disabledLinkStyle({ marginRight: 8 }) : { marginRight: 8 }}>
                        Cancel
                    </Link>
                )}
                {item.canRemove && (
                    <Link role="button" aria-label={`Remove access ${item.role}`}
                        onClick={hasSelection ? undefined : (e) => { e.stopPropagation(); this._onRemoveBtnClick(item); }}
                        style={hasSelection ? disabledLinkStyle() : undefined}>
                        Remove
                    </Link>
                )}
            </div>
        );
    }

    private _resolvePermissionsBusinessGroup(item: LifecycleGridItem): string {
        if (item.businessGroup && item.businessGroup.trim() !== '') {
            return item.businessGroup;
        }

        const scope = item.scope || '';
        const marker = `${UIConstants.Group}:`;
        const idx = scope.indexOf(marker);
        if (idx >= 0) {
            const value = scope.substring(idx + marker.length).split('|')[0].trim();
            if (value) {
                return value;
            }
        }

        return '';
    }

    private _renderPermissionsColumn(item: LifecycleGridItem): JSX.Element {
        const bgName = this._resolvePermissionsBusinessGroup(item);
        const normalize = (value?: string) => (value || '').toLowerCase().replace(/\s+/g, '');
        const isGlobalRole = normalize(bgName) === normalize(UIConstants.Global_Roles);
        const isBusinessOwner = normalize(item.role) === normalize(UIConstants.BusinessOwner);

        if (!item.roleId || item.role?.includes('Bulk Request') || isGlobalRole || isBusinessOwner) {
            return <div>NA</div>;
        }

        return (
            <div className={classNames.item}>
                <Link
                    role="button"
                    aria-label="permissions"
                    onClick={() => {
                        this.setState({
                            showPermissionsModal: true,
                            clickedBusinessGrp: bgName,
                            clickedRoleName: item.role,
                            clickedRoleId: item.roleId || null,
                        });
                    }}
                >
                    View Permissions
                </Link>
            </div>
        );
    }

    private _onRenderItemColumn = (item: LifecycleGridItem, index: number, column: IColumn): JSX.Element | React.ReactText => {
        if (column.key === 'status') {
          const isAttestationExpired = item.requestType?.toUpperCase() === UIConstants.RequestType.AttestationExpired.toUpperCase();
            const onOpenDetails = () => {
                this.setState({ showModal: true, requestId: item.requestId, data: item, requestedBy: item.requestedBy });
            };

          if (isAttestationExpired) {
            return (
              <div className={classNames.item}>
                <Link
                  role="button"
                  title={UIConstants.RequestType.AttestationExpired}
                  className={classNames.statusAttestationExpired}
                  onClick={onOpenDetails}
                >
                  <span className={classNames.srOnly}>Status: </span>
                  <span>{UIConstants.RequestType.AttestationExpired}</span>
                  <span className={classNames.srOnly}>{` for request ${item.requestId}`}</span>
                </Link>
              </div>
            );
          }

            return (
                <div className={classNames.item}>
                    <Link role="button" aria-label={`${item.status} ${item.requestId}`} onClick={onOpenDetails} > {item.status} </Link>
                </div>
            );
        }
        if (column.key === 'completedDate') {
            return (
                <span role="cell" aria-roledescription={item.completedDate === '-' ? 'Not Applicable' : item.completedDate}>{item.completedDate}</span>
            );
        }
        if (column.key === 'endDate') {
            return this._renderEndDateColumn(item);
        }
        if (column.key === 'requestedFor') {
            return this._renderRequestedForColumn(item);
        }
        if (column.key === 'action') {
            return this._renderActionColumn(item);
        }
        if (column.key === 'permissions') {
            return this._renderPermissionsColumn(item);
        }
        if (column.key === 'scope') {
            const parts = (item.scope || '').split('|').map(p => p.trim()).filter(Boolean);
            if (parts.length <= 1) return <span>{item.scope}</span>;
            return (
                <div>
                    {parts.map((p, i) => {
                        const colonIdx = p.indexOf(':');
                        if (colonIdx < 0) return <div key={i}>{p}</div>;
                        const label = p.substring(0, colonIdx + 1);
                        const value = p.substring(colonIdx + 1);
                        return <div key={i}><strong>{label}</strong>{value}</div>;
                    })}
                </div>
            );
        }
        return item[column.key];
    };

    private _onCancelBtnClick = (item: LifecycleGridItem): void => {
        this.setState({ showCancelConfirmation: true, showRemoveConfirmation: false, requestIdToCancel: item.requestId });
    };

    private _onRemoveBtnClick = (item: LifecycleGridItem): void => {
        this.setState({ showRemoveConfirmation: true, showCancelConfirmation: false, roleToRemove: item });
    };

    private _dismissAction = (): void => {
        this.setState({ showRemoveConfirmation: false, showCancelConfirmation: false, roleToRemove: null, requestIdToCancel: null });
    };

    private _runActionWithFeedback = async (
        action: (updateRowExecutionState: RowExecutionUpdater) => Promise<BatchExecutionResult>,
        successMessage: string,
        fallbackErrorMessage: string,
        refreshOnSuccess: boolean = true
    ): Promise<void> => {
        let lastMessage = '';
        let hasFailure = false;

        const result = await action((_id, status, message) => {
            lastMessage = message ?? '';
            hasFailure = status === 'failed';
        });

        if (!this._isMounted) {
            return;
        }

        if (result.successCount > 0 && !hasFailure) {
            this.safeSetState({
                actionInProgress: false,
                showMessage: true,
                message: successMessage,
                messageType: MessageBarType.success
            });
            if (refreshOnSuccess) {
                this.ensureDataFetched();
            }
            return;
        }

        this.safeSetState({
            actionInProgress: false,
            showMessage: true,
            message: lastMessage || fallbackErrorMessage,
            messageType: MessageBarType.error
        });
    };

    private _executeCancel = async (requestId: string | null): Promise<void> => {
        this.setState({ showCancelConfirmation: false, actionInProgress: true });
        if (!requestId) {
            this.setState({ actionInProgress: false });
            return;
        }

        const allItems = this._buildLifecycleItems();
        const targetItem = allItems.find(item => item.requestId === requestId);
        if (!targetItem) {
            this.setState({ actionInProgress: false, showMessage: true, message: UIConstants.Messages.RemoveFailure, messageType: MessageBarType.error });
            return;
        }

        await this._runActionWithFeedback(
            (updateRowExecutionState) => executeCancelSelected({
                selectedItems: [targetItem],
                updateRowExecutionState
            }),
            'Request cancelled successfully.',
            UIConstants.Messages.RemoveFailure
        );
    };

    private _executeRemove = async (item: LifecycleGridItem | null): Promise<void> => {
        this.setState({ showRemoveConfirmation: false, actionInProgress: true });
        if (!item?.roleId) {
            this.setState({ actionInProgress: false });
            return;
        }

        const allItems = this._buildLifecycleItems();
        const roleAssignmentId = this.state.currentAccessMap[item.roleId] ?? item.roleAssignmentId ?? null;
        const selectedItem = { ...item, roleAssignmentId } as LifecycleGridItem;

        await this._runActionWithFeedback(
            (updateRowExecutionState) => executeRemoveSelected({
                selectedItems: [selectedItem],
                tenantId: this.tenantData.TenantId,
                isExternalAADTenant: this.tenantData.isExternalAADTenant,
                allItems,
                approvedAccesses: this.state.approvedAccesses,
                updateRowExecutionState
            }),
            UIConstants.Messages.RemoveSuccess,
            UIConstants.Messages.RemoveFailure
        );
    };

    private _navigateToRequestAccess = (): void => {
        this.props.history.push(`/${UIConstants.RouteConfig.RequestRoot}/${UIConstants.RouteConfig.RequestAccess}/${this.tenantData?.TenantName}`);
    };

    private _clearSelection = (): void => {
        this.safeSetState({ selectedItems: [] });
    };

    private _updateRowExecutionState = (requestId: string, status: 'idle' | 'success' | 'failed', message?: string): void => {
        if (!this._isMounted) return;
        this.setState(prev => ({
            rowExecutionStates: {
                ...prev.rowExecutionStates,
                [requestId]: { status, message },
            },
        }));
    };

    private _executeBatchCancel = async (): Promise<void> => {
        const { selectedItems } = this.state;
        if (!selectedItems.length) return;
        this.safeSetState({ batchInProgress: true });

        await executeCancelSelected({
            selectedItems,
            updateRowExecutionState: this._updateRowExecutionState,
        });

        if (!this._isMounted) return;

        this._clearSelection();
        this.safeSetState({ batchInProgress: false });
        this._scheduleFeedbackAutoClear(true); // Cancel: reload to reflect status change
    };

    private _executeBatchRemove = async (): Promise<void> => {
        const { selectedItems } = this.state;
        if (!selectedItems.length) return;
        this.safeSetState({ batchInProgress: true });

        const allItems = this._buildLifecycleItems();
        const enrichedItems = selectedItems.map(item => ({
            ...item,
            roleAssignmentId: this.state.currentAccessMap[item.roleId] ?? item.roleAssignmentId ?? null,
        })) as LifecycleGridItem[];

        await executeRemoveSelected({
            selectedItems: enrichedItems,
            tenantId: this.tenantData.TenantId,
            isExternalAADTenant: this.tenantData.isExternalAADTenant,
            allItems,
            approvedAccesses: this.state.approvedAccesses,
            updateRowExecutionState: this._updateRowExecutionState,
        });

        if (!this._isMounted) return;

        this._clearSelection();
        this.safeSetState({ batchInProgress: false });
        this._scheduleFeedbackAutoClear(true); // Remove: reload to reflect access changes
    };

    private _scheduleFeedbackAutoClear = (shouldReloadData: boolean): void => {
        if (this._feedbackTimer) clearTimeout(this._feedbackTimer);
        this._feedbackTimer = setTimeout(() => { this._clearRowFeedback(shouldReloadData); }, 4000);
    };

    private _clearRowFeedback = (reloadData = true): void => {
        this.safeSetState({ rowExecutionStates: {}, selectedItems: [] });
        if (reloadData && this._isMounted) this.ensureDataFetched();
    };

    private _executeEditOrRenew = async (
        item: LifecycleGridItem,
        mode: 'edit' | 'renew'
    ): Promise<void> => {
        this.setState({ actionInProgress: true });
        const allItems = this._buildLifecycleItems();

        const executor = mode === 'edit' ? executeEditSelected : executeRenewSelected;
        const successMessage = mode === 'edit'
            ? 'Redirecting to Access Request for edit'
            : 'Redirecting to Access Request for renew';
        const failureMessage = mode === 'edit'
            ? 'Unable to start edit flow'
            : 'Unable to start renew flow';

        await this._runActionWithFeedback(
            (updateRowExecutionState) => executor({
                item: { ...item } as LifecycleGridItem,
                tenantName: this.tenantData?.TenantName,
                navigateToRequestAccess: this._navigateToRequestAccess,
                allItems,
                accessExpiryReminderWindowDays: this.state.accessExpiryReminderWindowDays,
                approvedAccesses: this.state.approvedAccesses,
                updateRowExecutionState,
            }),
            successMessage,
            failureMessage,
            false
        );
    };

    private _executeEdit = async (item: LifecycleGridItem): Promise<void> => {
        await this._executeEditOrRenew(item, 'edit');
    };

    private _executeRenew = async (item: LifecycleGridItem): Promise<void> => {
        await this._executeEditOrRenew(item, 'renew');
    };

    private _buildLifecycleItems(): LifecycleGridItem[] {
        return buildLifecycleItems({
            accessData: this.state.AccessData,
            userDetails: this.state.userDetails,
            tenantName: this.tenantData?.TenantName,
            currentAccessMap: this.state.currentAccessMap,
            approvedAccesses: this.state.approvedAccesses,
            accessExpiryReminderWindowDays: this.state.accessExpiryReminderWindowDays,
            roleEligibilityMap: this.state.roleEligibilityMap,
        });
    }

    private getItems() {
        const { searchValue } = this.state;
        const lifecycleItems = this._buildLifecycleItems();
        let filteredItems = lifecycleItems;
        if (searchValue && searchValue.trim() !== '') {
            filteredItems = lifecycleItems.filter(item => item.requestId && item.requestId.toString().toLowerCase().includes(searchValue.trim().toLowerCase()));
        }
        let sortedItems = this._copyAndSort(filteredItems, this.state.sortColumnKey, this.state.isSortDescending);
        return sortedItems;
    }

    private _onColumnClick = (event: React.MouseEvent<HTMLElement>, column: IColumn): void => {
        if (!column.fieldName) {
            return;
        }

        const isSortDescending = this.state.sortColumnKey === column.fieldName
            ? !this.state.isSortDescending
            : false;

        this.setState({
            sortColumnKey: column.fieldName,
            isSortDescending
        });
    }

    private getColumnDefintion(gridItems: LifecycleGridItem[] = [], selectedSet: Set<string> = new Set()) {
        const { batchInProgress } = this.state;

        const selectCol = {
            key: 'select',
            name: '',
            fieldName: 'select',
            minWidth: 32,
            maxWidth: 32,
            isResizable: false,
            onRenderHeader: () => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 42, color: '#605e5c', fontSize: 14 }} title="Select individual rows to batch-cancel or remove">
                    —
                </div>
            ),
            onRender: (item: LifecycleGridItem) => {
                const isBatchEligible = !!(item.canCancel || item.canRemove);
                if (!isBatchEligible) return null;
                const isSelected = selectedSet.has(item.requestId);
                return (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (batchInProgress) return;
                            const { selectedItems: current } = this.state;
                            const next = isSelected
                                ? current.filter(s => s.requestId !== item.requestId)
                                : [...current, item];
                            this.setState({ selectedItems: next });
                        }}
                        style={{ background: 'none', border: 'none', cursor: batchInProgress ? 'not-allowed' : 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label={`Select row ${item.requestId}`}
                    >
                        <Check checked={isSelected} />
                    </button>
                );
            }
        };

        let cols: any[] = [
            selectCol,
            { key: 'requestId', name: 'Request ID', fieldName: 'requestId', minWidth: 200, maxWidth: 200, isResizable: false },
            { key: 'role', name: 'Role', fieldName: 'role', minWidth: 180, maxWidth: 200, isResizable: false },
            { key: 'requestType', name: 'Request Type', fieldName: 'requestType', minWidth: 100, maxWidth: 100, isResizable: true },
            { key: 'requestedBy', name: 'Requested By', fieldName: 'requestedBy', minWidth: 100, maxWidth: 100, isResizable: true },
            { key: 'requestedFor', name: 'Requested For', fieldName: 'requestedFor', minWidth: 160, maxWidth: 200, isResizable: true, onRender: this._onRenderItemColumn },
            { key: 'scope', name: 'Scope', fieldName: 'scope', minWidth: 180, maxWidth: 280, isResizable: true, onRender: this._onRenderItemColumn },
            { key: 'requestDate', name: 'Requested Date', fieldName: 'requestDate', minWidth: Utility.getDynamicColumnSize(100), maxWidth: Utility.getDynamicColumnSize(160), isResizable: false },
            { key: 'completedDate', name: 'Completed Date', fieldName: 'completedDate', minWidth: Utility.getDynamicColumnSize(100), maxWidth: Utility.getDynamicColumnSize(160), isResizable: false },
            { key: 'status', name: 'Status', fieldName: 'status', minWidth: 120, maxWidth: 120, isResizable: false, onRender: this._onRenderItemColumn },
            { key: 'action', name: 'Action', fieldName: 'action', minWidth: 90, maxWidth: 110, isResizable: false, onRender: this._onRenderItemColumn },
        ];

        if (this.tenantData && !isMercuryTenant(this.tenantData.TenantName) && !isMSSalesTenant(this.tenantData.TenantName)) {
            cols.splice(8, 0, {
                key: 'endDate', name: 'Expire Date', fieldName: 'endDate',
                minWidth: Utility.getDynamicColumnSize(100), maxWidth: Utility.getDynamicColumnSize(160),
                isResizable: false, onRender: this._onRenderItemColumn
            });
        }

        if (this.state.hasRolePermissions) {
            cols.push({
                key: 'permissions',
                name: 'Permissions',
                fieldName: 'permissions',
                minWidth: 110,
                maxWidth: 120,
                isResizable: false,
                onRender: this._onRenderItemColumn,
            });
        }

        cols = cols.map((column) => ({
            ...column,
            isSorted: this.state.sortColumnKey === column.fieldName,
            isSortedDescending: this.state.sortColumnKey === column.fieldName ? this.state.isSortDescending : false,
        }));

        return cols;
    }

    private _copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
        const key = columnKey as keyof T;
        if (columnKey === 'completedDate' || columnKey === 'requestDate') {
            const rawKey = columnKey === 'requestDate' ? 'originalRequestedDate' : 'originalCompletedDate';
            return items.slice(0).sort((a: T, b: T) => {
                const aRaw = (a as any)[rawKey];
                const bRaw = (b as any)[rawKey];
                const aTime = aRaw ? new Date(aRaw).getTime() : 0;
                const bTime = bRaw ? new Date(bRaw).getTime() : 0;
                return isSortedDescending ? bTime - aTime : aTime - bTime;
            });
        } else if (columnKey === 'requestedFor') {
            // Sort by effective displayed value: requestedFor ?? requestedBy
            return items.slice(0).sort((a: T, b: T) => {
                const aVal = ((a as any).requestedFor ?? (a as any).requestedBy ?? '').toLowerCase();
                const bVal = ((b as any).requestedFor ?? (b as any).requestedBy ?? '').toLowerCase();
                if (aVal === bVal) return 0;
                return isSortedDescending ? (aVal < bVal ? 1 : -1) : (aVal > bVal ? 1 : -1);
            });
        } else {
            return items.slice(0).sort((a: T, b: T) => ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1));
        }
    }
}
